import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import Button from '../../../components/ui/Button.jsx';
import { getComplianceSeries } from '../../../utils/api';

const periods = [
  { value: '7d', label: '7 días' },
  { value: '30d', label: '30 días' },
  { value: '90d', label: '90 días' },
  { value: '1y', label: '1 año' }
];

const metrics = [
  { value: 'accuracy', label: 'Precisión', color: 'hsl(var(--success))' },
  { value: 'compliance', label: 'Cumplimiento', color: 'hsl(var(--primary))' },
  { value: 'quality', label: 'Calidad', color: 'hsl(var(--warning))' }
];

function ddmmyyyy(dateStr) {
  // acepta YYYY-MM-DD, YYYY-Wxx o YYYY-MM
  if (!dateStr) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y,m,d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  }
  return dateStr; // semanas/meses se muestran como vienen
}

function exportCsv(rows) {
  if (!rows?.length) return;
  const headers = ['period','accuracy','compliance','quality'];
  const lines = [headers.join(',')]
    .concat(rows.map(r => [r.period, r.accuracy, r.compliance, r.quality].join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `quality-trends_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const QualityTrendsChart = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('accuracy');
  const [state, setState] = useState({ loading: true, error: null, rows: [] });

  useEffect(() => {
    (async () => {
      try {
        setState(s => ({ ...s, loading: true, error: null }));
        const rows = await getComplianceSeries({ period: selectedPeriod });
        setState({ loading: false, error: null, rows: rows || [] });
      } catch (e) {
        console.error('[QualityTrendsChart] load error', e);
        setState({ loading: false, error: e, rows: [] });
      }
    })();
  }, [selectedPeriod]);

  const target = selectedMetric === 'quality' ? 8.5 : 95;
  const domainY = selectedMetric === 'quality' ? [0, 10] : [0, 100];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const point = payload.find(p => p.dataKey === selectedMetric) || payload[0];
      const value = point?.value ?? 0;
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">Período: {ddmmyyyy(label)}</p>
          <p className="text-sm" style={{ color: point?.color }}>
            {metrics.find(m => m.value === selectedMetric)?.label}: {selectedMetric === 'quality' ? `${value}/10` : `${value}%`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="mb-4 sm:mb-0">
          <h3 className="text-lg font-semibold text-foreground mb-1">
            Tendencias de Calidad
          </h3>
          <p className="text-sm text-muted-foreground">
            Evolución de precisión (mediciones), cumplimiento (calibraciones) y score de calidad.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex bg-muted rounded-lg p-1">
            {periods.map((p) => (
              <Button
                key={p.value}
                variant={selectedPeriod === p.value ? "default" : "ghost"}
                size="sm"
                onClick={() => setSelectedPeriod(p.value)}
                className="text-xs"
              >
                {p.label}
              </Button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={() => exportCsv(state.rows)}
            disabled={!state.rows.length}
            title="Exportar CSV"
          >
            Exportar
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex flex-wrap gap-4">
          {metrics.map((m) => (
            <button
              key={m.value}
              onClick={() => setSelectedMetric(m.value)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedMetric === m.value
                  ? 'bg-primary/10 text-primary border border-primary/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m.color }} />
              <span>{m.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="h-80">
        {state.loading ? (
          <div className="h-full grid place-items-center text-sm text-muted-foreground">Cargando…</div>
        ) : state.error ? (
          <div className="h-full grid place-items-center text-sm text-destructive">
            Error al cargar: {state.error.message}
          </div>
        ) : state.rows.length === 0 ? (
          <div className="h-full grid place-items-center">
            <div className="text-center p-8 border border-dashed rounded-xl">
              <h4 className="font-semibold">Sin datos</h4>
              <p className="text-sm text-muted-foreground">No hay actividad en el período seleccionado.</p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={state.rows} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis
                dataKey="period"
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickFormatter={ddmmyyyy}
              />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                domain={domainY}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />

              <ReferenceLine
                y={target}
                stroke="hsl(var(--error))"
                strokeDasharray="5 5"
                label={{ value: "Límite mínimo", position: "topRight" }}
              />

              {metrics.map((m) => (
                <Line
                  key={m.value}
                  type="monotone"
                  dataKey={m.value}
                  stroke={m.color}
                  strokeWidth={selectedMetric === m.value ? 3 : 2}
                  dot={{ fill: m.color, strokeWidth: 2, r: selectedMetric === m.value ? 5 : 3 }}
                  activeDot={{ r: 6, stroke: m.color, strokeWidth: 2 }}
                  name={m.label}
                  opacity={selectedMetric === m.value ? 1 : 0.35}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default QualityTrendsChart;
